import React, { useState, useEffect } from 'react';

import useSmartBack from '@hooks/shared/useSmartBack';
import FloatingActionBar, { FloatingActionBarSpacer } from '@components/shared/ui/FloatingActionBar';
import PageHeader from '@shared/PageHeader';
import { Button, Form, Input, message, Spin, Select, Row, Col } from 'antd';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { getCountryOptions, TWZIP } from '@constants/index';

interface ServiceResponse<T = unknown> {
  success: boolean;
  data?: T;
  [key: string]: unknown;
}

interface ContactAddressRecord {
  id?: string;
  merchant_id?: string | null;
  merchant?: { id?: string; name?: string } | null;
  [key: string]: unknown;
}

interface ContactAddressEditService {
  get: (id: string) => Promise<ServiceResponse<ContactAddressRecord>>;
  create: (data: Record<string, unknown>, merchantId?: string | null) => Promise<ServiceResponse<{ id?: string }>>;
  update: (id: string, data: Record<string, unknown>) => Promise<ServiceResponse<{ id?: string }>>;
  [key: string]: unknown;
}

interface SalesChannel {
  id: string;
  name: string;
  [key: string]: unknown;
}

interface ContactAddressEditProps {
  contactAddressService: ContactAddressEditService;
  role: 'admin' | 'operator';
  /**
   * 銷售管道下拉選項。由 wrapper 從 OperatorDataContext.merchantSalesChannels 或
   * admin 端 salesChannelService 載入後傳入，這支共用元件不再自行打 list API。
   * 改成由 wrapper 控制是為了讓「editing existing record」場景能保證 channels
   * 是「該筆 contact_address 所屬 merchant」的清單，而不是「使用者 header 當下選的 merchant」的清單。
   */
  salesChannels: SalesChannel[];
  /**
   * 該筆 contact_address 所屬的 merchant_id。
   * - NEW（新增）：建立時要綁的 merchant_id；operator 端為 currentMerchant.id
   *   （MerchantSelect 保證已選），admin 端傳 null（admin 後端從 session 自取 merchant scope）。
   * - EDIT（編輯）：已載入 record 的所屬 merchant_id；wrapper 透過
   *   onContactAddressLoaded → setCurrentMerchant 把 OperatorDataContext 對齊後，這支
   *   元件本身不需要再讀此 prop（update 走 record 既有 merchant），但語義上仍代表
   *   「這張表單操作的 merchant scope」。
   */
  merchantId?: string | null;
  /**
   * 載入完 record 後通知 wrapper，讓 wrapper 可以把 OperatorDataContext.currentMerchant
   * 切到該筆所屬 merchant，避免下拉用錯 merchant 過濾。
   */
  onContactAddressLoaded?: (record: ContactAddressRecord) => void;
  /**
   * Wrapper 把 merchant context 切換尚未完成（例如剛從 record 拿到 merchant_id、
   * 但 currentMerchant 還沒對齊）的狀態傳入，期間擋住表單渲染避免閃錯的下拉。
   */
  merchantDataLoading?: boolean;
}

const ContactAddressEdit: React.FC<ContactAddressEditProps> = ({
  contactAddressService,
  role,
  salesChannels,
  merchantId = null,
  onContactAddressLoaded,
  merchantDataLoading = false,
}) => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const goBack = useSmartBack();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  // 區分「儲存並新增下一筆」的 loading
  const [savingAndAdding, setSavingAndAdding] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const isEdit = !!id && id !== 'new';

  // 跟 FormReceiverInformation 相同邏輯：country=TW（或空）時，city/district 走 TWZIP 下拉並自動帶 postal_code
  const countryOptions = getCountryOptions(i18n.language);
  const country = Form.useWatch('country_code', form);
  const city = Form.useWatch('city', form);
  const district = Form.useWatch('district', form);
  const isTaiwanOrEmpty = !country || country === '' || country === 'TW';

  useEffect(() => {
    if (isTaiwanOrEmpty && city && district) {
      const cityData = (TWZIP as Record<string, Record<string, string>>)[city];
      if (cityData && cityData[district]) {
        const autoPostalCode = cityData[district];
        const currentPostalCode = form.getFieldValue('postal_code');
        if (autoPostalCode !== currentPostalCode) {
          form.setFieldsValue({ postal_code: autoPostalCode });
        }
      }
    }
  }, [city, district, isTaiwanOrEmpty, form]);

  const getCityOptions = () => Object.keys(TWZIP).map(cityName => ({ value: cityName, label: cityName }));

  const getDistrictOptions = () => {
    const zipMap = TWZIP as Record<string, Record<string, string>>;
    if (!city || !zipMap[city]) return [];
    return Object.keys(zipMap[city]).map(d => ({ value: d, label: d }));
  };

  const handleCityChange = (value: string) => {
    form.setFieldsValue({ city: value, district: '', postal_code: '' });
  };

  useEffect(() => {
    if (isEdit) {
      loadContactAddress();
    }
  }, [id]);

  const loadContactAddress = async () => {
    if (!id) return;
    setInitialLoading(true);
    try {
      const data = await contactAddressService.get(id);
      if (data.success) {
        const record = (data.data || {}) as ContactAddressRecord;
        form.setFieldsValue(record);
        onContactAddressLoaded?.(record);
      } else {
        message.error(t('common.message.load_failed'));
      }
    } catch (error) {
      console.error('Failed to load contact address:', error);
      message.error(t('common.message.load_failed'));
    } finally {
      setInitialLoading(false);
    }
  };
  // 核心提交邏輯：呼叫 create/update 並回傳 response 給呼叫者決定後續行為
  const submitValues = async (values: Record<string, unknown>) => {
    return isEdit && id
      ? await contactAddressService.update(id, values)
      : await contactAddressService.create(values, merchantId);
  };

  // 主要提交：成功後導向 show 頁（新建與編輯皆適用）
  const handleSubmit = async (values: Record<string, unknown>) => {
    setLoading(true);
    try {
      const data = await submitValues(values);

      if (data.success) {
        message.success(t(isEdit ? 'common.message.update_success' : 'common.message.create_success'));
        // 導向該筆記錄的 show 頁，basename 會自動補上 /operator 或 /admin
        const recordId = data.data?.id || id;
        navigate(`/contact_addresses/${recordId}`, { replace: true });
      } else {
        message.error(t(isEdit ? 'common.message.update_failed' : 'common.message.create_failed'));
      }
    } catch (error) {
      console.error('Failed to save contact address:', error);
      message.error(t(isEdit ? 'common.message.update_failed' : 'common.message.create_failed'));
    } finally {
      setLoading(false);
    }
  };

  // 儲存並新增下一筆：僅在新建模式可用，成功後重置表單留在本頁
  const handleSaveAndAddAnother = async () => {
    try {
      const values = await form.validateFields();
      setSavingAndAdding(true);
      try {
        const data = await submitValues(values);

        if (data.success) {
          message.success(t('common.message.create_success'));
          form.resetFields();
        } else {
          message.error(t('common.message.create_failed'));
        }
      } catch (error) {
        console.error('Failed to save contact address:', error);
        message.error(t('common.message.create_failed'));
      } finally {
        setSavingAndAdding(false);
      }
    } catch {
      // validateFields 失敗會 reject，antd 會自動高亮錯誤欄位
    }
  };

  const basePath = role === 'operator' ? '/operator' : '/admin';
  const lastItem = { label: isEdit ? t('common.button.edit') : t('common.button.create') };
  const breadcrumbItems = role === 'operator'
    ? ['merchants', 'merchants.contact_addresses', lastItem]
    : ['data', 'data.contact_addresses', lastItem];

  if (initialLoading || merchantDataLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }
  return (
    <div>
      <PageHeader
        basePath={basePath}
        items={breadcrumbItems}
      />
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        autoComplete="off"
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="code"
              label={t('models.contact_address.code')}
              tooltip={t('shared.contact_addresses.form.code_tooltip')}
              rules={[
                { required: true, message: t('common.validation.required') },
                { max: 50, message: t('common.validation.max_length', { count: 50 }) }
              ]}
            >
              <Input placeholder={t('common.placeholder.please_enter')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="default_sales_channel_id"
              label={t('models.contact_address.default_sales_channel')}
            >
              <Select
                showSearch
                allowClear
                placeholder={t('common.placeholder.please_select')}
                options={salesChannels.map(channel => ({
                  label: channel.name,
                  value: channel.id
                }))}
                optionFilterProp="label"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="receiver_name"
              label={t('models.contact_address.receiver_name')}
              rules={[
                { required: true, message: t('common.validation.required') },
                { max: 100, message: t('common.validation.max_length', { count: 100 }) }
              ]}
            >
              <Input placeholder={t('common.placeholder.please_enter')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="receiver_phone"
              label={t('models.contact_address.receiver_phone')}
              rules={[
                { required: true, message: t('common.validation.required') },
                { max: 20, message: t('common.validation.max_length', { count: 20 }) }
              ]}
            >
              <Input placeholder={t('common.placeholder.please_enter')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="country_code"
              label={t('models.contact_address.country_code')}
            >
              <Select
                placeholder={t('common.select.please_select')}
                allowClear
                showSearch
                optionFilterProp="label"
                options={countryOptions}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="state"
              label={t('models.contact_address.state', { defaultValue: '州 / 省' })}
              rules={[
                { max: 50, message: t('common.validation.max_length', { count: 50 }) }
              ]}
            >
              <Input placeholder={t('common.placeholder.please_enter')} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="city"
              label={t('models.contact_address.city')}
              rules={[
                { required: true, message: t('common.validation.required') },
                { max: 50, message: t('common.validation.max_length', { count: 50 }) }
              ]}
              getValueProps={(val) => ({ value: isTaiwanOrEmpty ? (val ? [val] : []) : val })}
              normalize={(val) => (Array.isArray(val) ? val[val.length - 1] || '' : val)}
            >
              {isTaiwanOrEmpty ? (
                <Select
                  mode="tags"
                  maxTagCount={1}
                  placeholder={t('common.select.please_select')}
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  options={getCityOptions()}
                  tokenSeparators={[]}
                  onChange={(values) => {
                    const v = Array.isArray(values) ? values[values.length - 1] || '' : values;
                    handleCityChange(v);
                  }}
                />
              ) : (
                <Input placeholder={t('common.placeholder.please_enter')} />
              )}
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="district"
              label={t('models.contact_address.district')}
              rules={[
                { max: 50, message: t('common.validation.max_length', { count: 50 }) }
              ]}
              getValueProps={(val) => ({ value: isTaiwanOrEmpty ? (val ? [val] : []) : val })}
              normalize={(val) => (Array.isArray(val) ? val[val.length - 1] || '' : val)}
            >
              {isTaiwanOrEmpty ? (
                <Select
                  mode="tags"
                  maxTagCount={1}
                  placeholder={t('common.select.please_select')}
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  options={getDistrictOptions()}
                  tokenSeparators={[]}
                />
              ) : (
                <Input placeholder={t('common.placeholder.please_enter')} />
              )}
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="postal_code"
              label={t('models.contact_address.postal_code')}
              rules={[
                { max: 10, message: t('common.validation.max_length', { count: 10 }) }
              ]}
            >
              <Input
                placeholder={isTaiwanOrEmpty && city && district ? t(`common.auto_filled`) : t('common.placeholder.please_enter')}
                disabled={isTaiwanOrEmpty && !!city && !!district}
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              name="address"
              label={t('models.contact_address.address')}
              rules={[
                { required: true, message: t('common.validation.required') },
                { max: 500, message: t('common.validation.max_length', { count: 500 }) }
              ]}
            >
              <Input.TextArea
                rows={3}
                placeholder={t('common.placeholder.please_enter')}
              />
            </Form.Item>
          </Col>
        </Row>

        <FloatingActionBarSpacer />
      </Form>

      <FloatingActionBar
        role={role}
        onCancel={goBack}
        onSubmit={() => form.submit()}
        loading={loading}
        disabled={savingAndAdding}
        extraButtons={!isEdit ? (
          <Button
            loading={savingAndAdding}
            disabled={loading}
            onClick={handleSaveAndAddAnother}
          >
            {t('common.button.save_and_add_another')}
          </Button>
        ) : null}
      />
    </div>
  );
};
export default ContactAddressEdit;
